export type LeaveAnswer = boolean | Promise<boolean>;

/**
 * Lets a host that shows one record at a time (a drawer) keep what it holds:
 * while the host holds the guard, anyone about to put another record in its
 * place asks first. Openers never learn why; the host never learns who opens.
 */
export type LeaveGuard<Target> = {
  /**
   * The host answers whether `target` may take its place (asking the user if
   * it must); returns the release.
   */
  hold(ask: (target: Target) => LeaveAnswer): () => void;
  /** `true` at once when nobody holds the guard, the holder's answer otherwise. */
  request(target: Target): LeaveAnswer;
};

export function createLeaveGuard<Target>(): LeaveGuard<Target> {
  let holder: ((target: Target) => LeaveAnswer) | null = null;
  return {
    hold(ask) {
      holder = ask;
      return () => {
        if (holder === ask) holder = null;
      };
    },
    request(target) {
      return holder ? holder(target) : true;
    }
  };
}
